import { HEATMAP_ALL_OPPORTUNITIES, MAP_COORD_DECIMALS } from './constants';

/** Visible map area, rounded, as shown to Scout. */
export interface MapViewBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** What a page's map shows, shared by every page with a map. */
export interface MapViewContext {
  mapZoom: number | null;
  mapBounds: MapViewBounds | null;
  activeMarker: string | null;
  heatmapMetric: string | null;
}

interface RawMapView {
  zoom: number | null | undefined;
  bounds:
    | { min_lat: number; max_lat: number; min_lng: number; max_lng: number }
    | null
    | undefined;
  activeMarker: number | string | null | undefined;
  heatmapOn: boolean;
  heatmapMetric: { label?: string | null } | null | undefined;
}

/** Round a coordinate so small pans don't change what Scout sees. */
function roundCoord(value: number): number | null {
  if (!Number.isFinite(value)) return null;
  const factor = 10 ** MAP_COORD_DECIMALS;
  return Math.round(value * factor) / factor;
}

/** Round the bounds, or null before the map has any (all zeros) or if invalid. */
function roundBounds(bounds: RawMapView['bounds']): MapViewBounds | null {
  if (!bounds) return null;
  const { min_lat, max_lat, min_lng, max_lng } = bounds;
  if ([min_lat, max_lat, min_lng, max_lng].every((v) => v === 0)) return null;
  const rounded = {
    north: roundCoord(max_lat),
    south: roundCoord(min_lat),
    east: roundCoord(max_lng),
    west: roundCoord(min_lng),
  };
  return Object.values(rounded).every((v) => v !== null)
    ? (rounded as MapViewBounds)
    : null;
}

/**
 * Build the map fields for a page context: rounded zoom and bounds, the
 * hovered or clicked marker, and the heatmap metric when the heatmap is on.
 */
export function buildMapViewContext({
  zoom,
  bounds,
  activeMarker,
  heatmapOn,
  heatmapMetric,
}: RawMapView): MapViewContext {
  return {
    mapZoom: Number.isFinite(zoom) ? Math.round(zoom) : null,
    mapBounds: roundBounds(bounds),
    activeMarker: activeMarker != null ? String(activeMarker) : null,
    // A heatmap on with no metric picked is the "All Opportunities" option.
    heatmapMetric: heatmapOn
      ? heatmapMetric?.label ?? HEATMAP_ALL_OPPORTUNITIES
      : null,
  };
}

/** Empty map fields, for when the map isn't on screen. */
export const NO_MAP_VIEW: MapViewContext = {
  mapZoom: null,
  mapBounds: null,
  activeMarker: null,
  heatmapMetric: null,
};
